import { TypeIconName } from '@bitrise/bitkit';

enum GitProvider {
  GITHUB = 'github',
  GITHUB_APP = 'github-app',
  GITHUB_SELF_HOSTED = 'github-self-hosted',
  GITLAB = 'gitlab',
  GITLAB_SELF_HOSTED = 'gitlab-self-hosted',
  BITBUCKET = 'bitbucket',
  BITBUCKET_SERVER = 'bitbucket-server',
  CUSTOM = 'custom',
}

export type GitProviderId = `${GitProvider}`;

const SCUSupportedProviders = [
  GitProvider.GITHUB,
  GitProvider.GITLAB,
  GitProvider.BITBUCKET,
  GitProvider.GITLAB_SELF_HOSTED,
];

const GHESSupportedProviders = [GitProvider.CUSTOM, GitProvider.GITHUB_SELF_HOSTED];

const ManualInputProviders = [GitProvider.GITHUB_SELF_HOSTED, GitProvider.BITBUCKET_SERVER, GitProvider.CUSTOM];

export const isProviderSCUSupported = (id: GitProvider | undefined): boolean =>
  id ? SCUSupportedProviders.includes(id) : false;
export const isProviderGHESSupported = (id: GitProvider | undefined): boolean =>
  id ? GHESSupportedProviders.includes(id) : false;
export const isProviderRequiresManualInput = (id: GitProvider | undefined) =>
  id ? ManualInputProviders.includes(id) : true;

export const getGitProviderName = (id: GitProvider | undefined): string | null => {
  switch (id) {
    case GitProvider.GITHUB:
    case GitProvider.GITHUB_APP:
      return 'GitHub';
    case GitProvider.GITHUB_SELF_HOSTED:
      return 'GitHub Enterprise Server';
    case GitProvider.GITLAB:
      return 'GitLab';
    case GitProvider.GITLAB_SELF_HOSTED:
      return 'Self-hosted GitLab';
    case GitProvider.BITBUCKET:
      return 'Bitbucket';
    case GitProvider.BITBUCKET_SERVER:
      return 'Bitbucket Server';
    case GitProvider.CUSTOM:
    default:
      return 'Custom git provider';
  }
};

export const getOnProviderText = (id: GitProvider | undefined): string => {
  switch (id) {
    case GitProvider.GITHUB:
    case GitProvider.GITHUB_APP:
      return 'on GitHub';
    case GitProvider.GITHUB_SELF_HOSTED:
      return 'on your GitHub Enterprise Server';
    case GitProvider.GITLAB:
      return 'on GitLab';
    case GitProvider.GITLAB_SELF_HOSTED:
      return 'on your Self-hosted GitLab instance';
    case GitProvider.BITBUCKET:
      return 'on Bitbucket';
    case GitProvider.BITBUCKET_SERVER:
      return 'on your Bitbucket Server';
    case GitProvider.CUSTOM:
    default:
      return 'on your git provider';
  }
};

export const getGitProviderPATSettingsPage = (id: GitProvider | undefined): string | null => {
  switch (id) {
    case GitProvider.GITHUB:
      return 'https://github.com/settings/tokens?type=beta';
    case GitProvider.GITLAB:
      return 'https://gitlab.com/-/profile/personal_access_tokens';
    case GitProvider.BITBUCKET:
      return 'https://bitbucket.org/account/settings/app-passwords/';
    default:
      return null;
  }
};

export const getGitProviderSSHKeySettingsPage = (id: GitProvider | undefined): string | null => {
  switch (id) {
    case GitProvider.GITHUB:
      return 'https://github.com/settings/keys';
    case GitProvider.GITLAB:
      return 'https://gitlab.com/-/profile/keys';
    case GitProvider.BITBUCKET:
      return 'https://bitbucket.org/account/settings/ssh-keys/';
    default:
      return null;
  }
};

export const isUsernameRequiredForHTTPConnection = (id: GitProvider | undefined): boolean => {
  switch (id) {
    case GitProvider.GITHUB:
      return false;
    case GitProvider.GITLAB:
      return false;
    default:
      return true;
  }
};

export const getGitProviderIconName = (id: GitProvider): TypeIconName => {
  switch (id) {
    case GitProvider.GITHUB:
    case GitProvider.GITHUB_APP:
    case GitProvider.GITHUB_SELF_HOSTED:
      return 'Github';
    case GitProvider.GITLAB:
    case GitProvider.GITLAB_SELF_HOSTED:
      return 'GitlabPlain';
    case GitProvider.BITBUCKET:
    case GitProvider.BITBUCKET_SERVER:
      return 'BitbucketPlain';
    case GitProvider.CUSTOM:
    default:
      return 'Globe';
  }
};

export const getViewOnProviderText = (id: GitProvider | undefined): string | undefined => {
  switch (id) {
    case GitProvider.GITHUB:
    case GitProvider.GITHUB_APP:
      return 'View it on GitHub';
    case GitProvider.GITLAB:
      return 'View it on GitLab';
    case GitProvider.BITBUCKET:
      return 'View it on Bitbucket';
    default:
      return undefined;
  }
};

export const getGitProviderPullRequestUrl = (
  id?: GitProvider,
  owner?: string,
  repoSlug?: string,
  pullRequestId?: number,
): string | undefined => {
  if (!id || !owner || !repoSlug || !pullRequestId) {
    return undefined;
  }

  switch (id) {
    case GitProvider.GITHUB:
    case GitProvider.GITHUB_APP:
      return `https://github.com/${owner}/${repoSlug}/pull/${pullRequestId}`;
    case GitProvider.GITLAB:
      return `https://gitlab.com/${owner}/${repoSlug}/-/merge_requests/${pullRequestId}`;
    case GitProvider.BITBUCKET:
      return `https://bitbucket.org/${owner}/${repoSlug}/pull-requests/${pullRequestId}`;
    default:
      return undefined;
  }
};

export const getGitProviderCommitHashUrl = (
  id?: GitProvider,
  owner?: string,
  repoSlug?: string,
  commitHash?: string,
  pullRequestId?: number,
): string | undefined => {
  if (!id || !owner || !repoSlug || !commitHash) {
    return undefined;
  }

  switch (id) {
    case GitProvider.GITHUB:
    case GitProvider.GITHUB_APP:
      if (pullRequestId) {
        return `https://github.com/${owner}/${repoSlug}/pull/${pullRequestId}/commits/${commitHash}`;
      }
      return `https://github.com/${owner}/${repoSlug}/commit/${commitHash}`;
    case GitProvider.GITLAB:
      return `https://gitlab.com/${owner}/${repoSlug}/-/commit/${commitHash}`;
    case GitProvider.BITBUCKET:
      return `https://bitbucket.org/${owner}/${repoSlug}/commits/${commitHash}`;
    default:
      return undefined;
  }
};

export const getGitProviderConnectPath = (id: GitProvider): string | undefined => {
  switch (id) {
    case GitProvider.GITHUB:
      return '/users/auth/github';
    case GitProvider.GITLAB:
      return '/users/auth/gitlab';
    case GitProvider.BITBUCKET:
      return '/users/auth/bitbucket';
    default:
      return undefined;
  }
};

export default GitProvider;
